import { ImagePlus, Mic, Paperclip, Send, Smile, Square, X } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';

export default function Composer({
  disabled,
  onSend,
  onTyping,
  replyPreview,
  onCancelReply,
}: {
  disabled?: boolean;
  onSend: (content: string, file: File | null) => Promise<void>;
  onTyping: () => void;
  replyPreview?: string | null;
  onCancelReply?: () => void;
}) {
  const [content, setContent] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<BlobPart[]>([]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if ((!content.trim() && !file) || sending || disabled) return;

    setSending(true);
    try {
      await onSend(content.trim(), file);
      setContent('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setSending(false);
    }
  }

  async function toggleRecording() {
    if (recording) {
      mediaRecorderRef.current?.stop();
      setRecording(false);
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      window.alert('Voice recording is not supported in this browser.');
      return;
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);
    audioChunksRef.current = [];

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) audioChunksRef.current.push(event.data);
    };

    recorder.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
      const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const voiceFile = new File([blob], `voice-message-${Date.now()}.webm`, { type: 'audio/webm' });
      setFile(voiceFile);
    };

    mediaRecorderRef.current = recorder;
    recorder.start();
    setRecording(true);
  }

  return (
    <form onSubmit={handleSubmit} className="relay-glass border-t border-white/10 p-3">
      {replyPreview && (
        <div className="mb-3 flex items-center justify-between rounded-xl border border-relay-cyan/25 bg-relay-cyan/10 px-3 py-2 text-sm text-slate-200">
          <span className="min-w-0 truncate">
            <span className="font-semibold text-relay-cyan">Replying to</span> {replyPreview}
          </span>
          <button
            type="button"
            onClick={onCancelReply}
            className="ml-3 rounded-lg p-1 text-slate-400 transition hover:bg-white/10 hover:text-white"
            aria-label="Cancel reply"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {file && (
        <div className="mb-3 flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-slate-300">
          <span className="min-w-0 truncate">
            <span className="font-semibold text-white">Upload</span> {file.name}
          </span>
          <button
            type="button"
            onClick={() => setFile(null)}
            className="ml-3 rounded-lg p-1 text-slate-500 transition hover:bg-white/10 hover:text-white"
            aria-label="Remove attachment"
          >
            <X size={16} />
          </button>
        </div>
      )}

      <div className="flex items-end gap-1 rounded-2xl border border-white/10 bg-black/25 p-2 shadow-[0_0_0_1px_rgba(255,255,255,.02)]">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*,audio/*,.pdf,.zip,.txt,.doc,.docx,.ppt,.pptx"
          className="hidden"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-slate-400 transition hover:bg-white/10 hover:text-white"
          title="Upload image, video, or file"
          aria-label="Upload attachment"
          disabled={disabled || sending}
        >
          <Paperclip size={18} />
        </button>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="hidden h-10 w-10 shrink-0 place-items-center rounded-xl text-slate-400 transition hover:bg-white/10 hover:text-white sm:grid"
          title="Upload media"
          aria-label="Upload media"
          disabled={disabled || sending}
        >
          <ImagePlus size={18} />
        </button>
        <button
          type="button"
          onClick={toggleRecording}
          className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl transition ${
            recording ? 'bg-relay-red/15 text-relay-red' : 'text-slate-400 hover:bg-white/10 hover:text-white'
          }`}
          title={recording ? 'Stop voice message' : 'Record voice message'}
          aria-label={recording ? 'Stop voice message' : 'Record voice message'}
          disabled={disabled || sending}
        >
          {recording ? <Square size={16} /> : <Mic size={18} />}
        </button>
        <textarea
          value={content}
          onChange={(event) => {
            setContent(event.target.value);
            onTyping();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }
          }}
          disabled={disabled || sending}
          rows={1}
          className="relay-scrollbar max-h-32 min-h-10 flex-1 resize-none border-0 bg-transparent px-2 py-2.5 text-sm text-white outline-none placeholder:text-slate-500"
          placeholder="Message #general"
        />
        <button
          type="button"
          className="hidden h-10 w-10 shrink-0 place-items-center rounded-xl text-slate-400 transition hover:bg-white/10 hover:text-white sm:grid"
          title="Emoji reactions"
          aria-label="Open emoji picker"
          disabled={disabled || sending}
        >
          <Smile size={18} />
        </button>
        <button
          type="submit"
          disabled={disabled || sending || (!content.trim() && !file)}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-r from-relay-blue to-relay-violet text-white shadow-glow transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Send message"
        >
          <Send size={18} />
        </button>
      </div>
    </form>
  );
}
